import {
  DirectSalesProductPoiFilter,
  DirectSalesProductTemplateCustomPoiFilterItem,
  ProductTemplateRulesResponse,
} from 'components/common/types/DirectSalesProductTemplate.types';
import { DirectSalesFilterScope } from 'components/common/types/DirectSalesCampaign.types';
import { POI } from 'components/common/types/Planner.types';
import { CUSTOM_POI_DATA_PROVIDER } from 'components/pages/Planner/PlannerSections/DataTargetingContent/PointsOfInterest/consts';
import { derivePoiDisplayFromProductTemplateCategoryPath } from './derivePoiDisplayFromProductTemplateCategoryPath';

export const getHasChildrenFromProductTemplatePoiName = (name: string | null | undefined): boolean => {
  if (name == null) {
    return true;
  }

  return name.trim() === '';
};

const isPoiFixedSet = (fixedSet: unknown): fixedSet is DirectSalesProductPoiFilter[] => {
  if (!Array.isArray(fixedSet) || fixedSet.length === 0) {
    return false;
  }

  return (fixedSet as DirectSalesProductPoiFilter[]).every(
    (poiItem: DirectSalesProductPoiFilter): poiItem is DirectSalesProductPoiFilter =>
      typeof poiItem === 'object' &&
      poiItem !== null &&
      'radius' in poiItem &&
      'selectionType' in poiItem &&
      'dataProvider' in poiItem,
  );
};

const isCustomPoiFixedSet = (fixedSet: unknown): fixedSet is DirectSalesProductTemplateCustomPoiFilterItem[] => {
  if (!Array.isArray(fixedSet) || fixedSet.length === 0) {
    return false;
  }

  return (fixedSet as DirectSalesProductTemplateCustomPoiFilterItem[]).every(
    (item) =>
      typeof item === 'object' &&
      item !== null &&
      'id' in item &&
      'selectionType' in item &&
      'values' in item &&
      Array.isArray(item.values),
  );
};

export const transformCustomPoi = (foundPoi: ProductTemplateRulesResponse): POI[] => {
  const fixedSet = foundPoi?.fixedSet ?? [];

  if (!isCustomPoiFixedSet(fixedSet)) {
    return [];
  }

  return fixedSet.flatMap((item) => {
    const fileName = item.id;
    const include = item.selectionType === DirectSalesFilterScope.INCLUDE;

    return item.values.map((value, index) => {
      const categoryPath = `${CUSTOM_POI_DATA_PROVIDER}/${fileName}/${value.name}-${index}`;

      return {
        code: fileName,
        name: value.name,
        include,
        radius: value.radius,
        dataProvider: CUSTOM_POI_DATA_PROVIDER,
        hasChildren: false,
        rootCategoryType: fileName,
        parentCategory: '',
        categoryPath,
        tooltip: `${fileName} > ${value.name}`,
        poiPoints: [
          {
            id: `custom-${categoryPath}`,
            name: value.name,
            geometry: { coordinates: [value.longitude, value.latitude] },
            properties: {
              categoryName: fileName,
              categoryType: fileName,
              address: '',
              postalCode: '',
              dataProvider: CUSTOM_POI_DATA_PROVIDER,
              mediaOwner: '',
              type: 'custom',
              visibility: { include, radius: value.radius },
            },
          },
        ],
      };
    });
  });
};

export const transformPoi = (foundPoi: ProductTemplateRulesResponse): POI[] => {
  const fixedSet = foundPoi?.fixedSet ?? [];

  if (!isPoiFixedSet(fixedSet)) {
    return [];
  }

  return fixedSet.map((poi) => {
    const categoryPath = poi.id;
    const poiCategoryPathDisplay = derivePoiDisplayFromProductTemplateCategoryPath(categoryPath);
    const name = poi.name || poiCategoryPathDisplay.leafName;

    return {
      include: poi.selectionType === DirectSalesFilterScope.INCLUDE,
      radius: poi.radius,
      dataProvider: poi.dataProvider,
      hasChildren: getHasChildrenFromProductTemplatePoiName(poi.name),
      rootCategoryType: poiCategoryPathDisplay.rootCategoryType,
      parentCategory: poiCategoryPathDisplay.parentCategory,
      categoryPath,
      tooltip: poiCategoryPathDisplay.tooltip,
      code: categoryPath,
      name,
    };
  });
};
